/* ============================================================================
   haiku-cursor — one cursor object, several states

   Two Codrops "Custom Cursors" demos (MIT, Codrops 2019, S. Kaltenegger)
   folded into a single layer. Read both before changing anything here,
   because the states are their mechanics, not an impression of them.

   DEMO 4 — "magnetic noisy circle" (index4.html)
     Two nodes: a 5px dot moved 1:1 with the pointer every frame, and a
     full-viewport <canvas> where paper.js draws an 8-segment regular polygon,
     smoothed into a circle. Each frame the polygon's centre is lerped toward
     the pointer at 0.2 — that lag IS the effect. On mouseenter of a target it
     records the target's centre and lerps toward THAT instead (isStuck), so
     the ring is magnetically captured. While stuck it multiplies its scale by
     1.08 a frame until 75px wide; when released, by 0.92 until 30px. Once
     stuck AND at full size it caches the 8 vertex positions once, then per
     frame per vertex reads two independent SimplexNoise walks at
     count/150, maps them to ±4px and offsets that vertex — so the circle
     breathes organically rather than pulsing as a unit. Releasing restores
     the cached vertices (skip that and the shape drifts permanently).
     .smooth() re-fits the bezier handles every frame. A separate flag swaps
     stroke for fill on nav items and fades the dot out.

   DEMO 2 — "navigation cursor" (index2.html)
     One node: a 150x110 wrapper, position fixed, pre-offset by half its size
     so its origin is its centre, holding an <svg> arrow. The wrapper's
     POSITION is set 1:1 from clientX/clientY every frame. The arrow's
     ROTATION / SCALE / OPACITY are tweened on the INNER element. That
     separation is the whole trick — the per-frame position write can never
     interrupt the spin. Entering the carousel picks a start rotation from the
     quadrant you entered through (-135 above the midline, else 135 or -315),
     sets it instantly, then tweens to 0 (pointing right) or -180 (left) with
     Back.easeOut(1.7) — an overshoot. Leaving spins back out to a mirrored
     angle at opacity 0, scale 0.3. Two invisible 50%-width halves overlay the
     carousel; entering either flips the arrow 180 degrees, so crossing the
     midline turns the arrow rather than swapping an icon. A slide change
     bumps the wrapper to 0.85 and back — a recoil.

   WHAT THEY HAVE IN COMMON is the actual system: a fixed, pointer-events-none,
   pre-centred object whose POSITION comes from a rAF loop reading the pointer,
   and whose APPEARANCE comes from hover state written to a different element.
   So: one object, one loop, one state attribute. Demo 4 is the `link` state,
   demo 2 is the `nav` state, and they are the same node.

   ADAPTATIONS, all deliberate:
     - No paper.js (~1MB) and no simplex-noise. The blob is an SVG path
       rebuilt each frame from N polar vertices and closed with a Catmull-Rom
       to cubic-bezier conversion, which is what paper's smooth() does. The
       noise is a small per-vertex value-noise walk with an independent seed
       per vertex, mirroring `segments.map(() => new SimplexNoise())`.
     - Radius eases by lerp, not by a per-frame multiply. Same feel, and not
       silently frame-rate dependent the way scale(1.08) is.
     - Demo 4 sticks to every target. Here it sticks only to targets smaller
       than STICK_MAX (default 120px). A gallery slide is most of the screen;
       sticking to its centre would fling the pointer's own cursor across the
       viewport. Large targets inflate the ring in place instead.
     - Rotation, scale and opacity are CSS transitions rather than a hand-
       rolled tween engine. The overshoot lives in a cubic-bezier knob.

   FAIL-SAFE, which is the load-bearing part:
     Nothing here hides the system cursor. The CSS rule that does is gated on
     html.hcu-on, added as the LAST statement of a try block, only after the
     nodes are in the DOM. Any throw before that leaves the native cursor. A
     watchdog then keeps checking that the frame loop is alive; if it stops —
     later error, tab throttling gone wrong, anything — the class comes off
     and the real pointer returns. The site is never left cursorless.
   ========================================================================== */
(function () {
  'use strict';

  var cfg = window.haikuCursorConfig || {};

  function num(v, d) { return typeof v === 'number' ? v : d; }
  function str(v, d) { return typeof v === 'string' && v ? v : d; }

  /* ── knobs (all overridable from snippets/haiku-cursor.liquid) ─────────── */

  var DOT          = num(cfg.dotSize, 4);
  var R_IDLE       = num(cfg.radiusIdle, 14);
  var R_LINK       = num(cfg.radiusLink, 26);
  var R_NAV        = num(cfg.radiusNav, 30);
  var FOLLOW       = num(cfg.follow, 0.18);
  var FOLLOW_STUCK = num(cfg.followStuck, 0.22);
  var RADIUS_EASE  = num(cfg.radiusEase, 0.16);
  var SEGMENTS     = Math.max(3, num(cfg.segments, 10) | 0);
  var NOISE_RANGE  = num(cfg.noiseRange, 1.6);
  var NOISE_SCALE  = num(cfg.noiseScale, 170);
  var STICK_MAX    = num(cfg.stickMax, 120);
  /* The product frame — see snippets/haiku-cursor.liquid. */
  var SEL_FRAME    = str(cfg.frameSelector, '');
  var FRAME_TARGET = str(cfg.frameTarget, '');
  var FRAME_PAD    = num(cfg.framePad, 6);
  var FRAME_RADIUS = num(cfg.frameRadius, 0);
  var FRAME_EASE   = num(cfg.frameEase, 0.16);
  /* The frame's line is drawn by hand, not ruled (zy, 2026-10-04: "can the
     wrapping line effect be more random?"). Three sources of irregularity:
       JITTER  a random shape per card — every vertex sits between ¼ of this
               inside and all of it outside its place on the box, corners
               always outward; a new card eases to a new shape;
       POINTS  vertices per side, the corner included, spaced unevenly —
               few points make long, loose bulges rather than a ripple;
       NOISE   the breathing: each vertex walks its own noise along the
               outward normal, NOISE_MS per noise period (time-based).
     Inward travel is at most ¼·JITTER + NOISE, kept under the pad, so the
     line never crosses into the photograph. */
  var FRAME_NOISE    = num(cfg.frameNoise, 3.5);
  var FRAME_JITTER   = num(cfg.frameJitter, 7);
  var FRAME_PTS      = Math.max(2, num(cfg.framePoints, 4) | 0);
  var FRAME_NOISE_MS = Math.max(200, num(cfg.frameNoiseMs, 2600));
  var FRAME_N        = FRAME_PTS * 4;
  var SEL_LINK     = str(cfg.interactiveSelector,
    'a[href], button, [role="button"], summary, label[for], input[type="submit"], .gallery__slide');
  var SEL_NAV      = str(cfg.navSelector, '.hp-nav');
  var SEL_PREV     = str(cfg.navPrevSelector, '.hp-nav__btn--prev');
  var SEL_NEXT     = str(cfg.navNextSelector, '.hp-nav__btn--next');
  var SEL_IGNORE   = str(cfg.ignoreSelector,
    'input, textarea, select, [contenteditable="true"], .hi-lb, iframe');

  /* ── gates ─────────────────────────────────────────────────────────────── */

  var mq = window.matchMedia;
  if (!mq) return;
  if (mq('(hover: none)').matches) return;
  if (mq('(pointer: coarse)').matches) return;
  if (mq('(prefers-reduced-motion: reduce)').matches) return;
  if (!document.body) return;

  var root = document.documentElement;

  /* ── pointer + object state ────────────────────────────────────────────── */

  var px = -400, py = -400;          /* raw pointer */
  var rx = -400, ry = -400;          /* ring centre, lerped */
  var radius = R_IDLE;
  var seen = false;                  /* first real pointer event */
  var stuck = null;                  /* {x, y} when magnetically captured */
  var state = 'off';
  var frameEl = null;                /* the box being framed while in frame state */
  var fw = 0, fh = 0, frx = 0;       /* the frame's size and corner, lerped */
  var ftw = 0, fth = 0;              /* what they are heading for */
  var shapedFor = null;              /* the box the current random shape was drawn for */
  var frameOn = false;               /* the frame is drawn instead of the blob */
  var navSide = null;                /* 'prev' | 'next' while in nav state */
  var frame = 0;
  var alive = 0;
  /* The loop sleeps when nothing on screen would change (2026-10-03): it used
     to rewrite the ring's path and both transforms every frame, moved or not
     — measured, ~2.5 ms of work a frame at rest, more than half of the page's
     idle work. `raf` is the scheduled frame (0 = asleep), `sleeping` tells the
     watchdog the silence is intended, and the last-written values let a
     running frame skip writes that would not change anything. */
  var raf = 0;
  var sleeping = false;
  var lastBlobD = '', lastFrameD = '', lastDotT = '', lastRingT = '';

  /* Independent noise seed per vertex — demo 4 gives every segment its own
     SimplexNoise instance, so the vertices wander out of step with each
     other. A shared seed would make the whole ring pulse in unison. */
  var seeds = [];
  for (var s = 0; s < SEGMENTS; s++) seeds.push(Math.random() * 1000);
  /* Per frame vertex: a noise seed (fixed), and a shape — an offset along
     the outward normal as a fraction of JITTER, and for the points between
     corners a place along the side — each with a current value that eases
     toward a target re-drawn for every card (reshape()). */
  var fseeds = [], fbias = [], fbiasT = [], fpos = [], fposT = [];
  function rand(a, b) { return a + Math.random() * (b - a); }
  function reshape() {
    for (var i = 0; i < FRAME_N; i++) {
      var q = i % FRAME_PTS;
      fbiasT[i] = q === 0 ? rand(0.2, 1.2) : rand(-0.25, 1);
      fposT[i] = q === 0 ? 0 : (q + rand(-0.35, 0.35)) / FRAME_PTS;
    }
  }
  reshape();
  for (var fs = 0; fs < FRAME_N; fs++) { fseeds.push(Math.random() * 1000); fbias.push(fbiasT[fs]); fpos.push(fposT[fs]); }

  /* 1-D value noise: hash two integer lattice points, smoothstep between
     them. Cheap, deterministic, and in the same -1..1 range as noise2D. */
  function hash(n) {
    var x = Math.sin(n * 127.1) * 43758.5453;
    return (x - Math.floor(x)) * 2 - 1;
  }
  function noise(t) {
    var i = Math.floor(t), f = t - i;
    var u = f * f * (3 - 2 * f);
    return hash(i) * (1 - u) + hash(i + 1) * u;
  }

  /* ── the object ────────────────────────────────────────────────────────── */

  var NS = 'http://www.w3.org/2000/svg';

  var dot = document.createElement('div');
  dot.className = 'hcu-dot';
  dot.setAttribute('aria-hidden', 'true');
  dot.style.setProperty('--hcu-dot', DOT + 'px');
  var dotI = document.createElement('div');
  dotI.className = 'hcu-dot__i';
  dot.appendChild(dotI);

  var ring = document.createElement('div');
  ring.className = 'hcu-ring';
  ring.setAttribute('aria-hidden', 'true');

  var svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('class', 'hcu-ring__svg');
  svg.setAttribute('viewBox', '-100 -100 200 200');
  var blob = document.createElementNS(NS, 'path');
  blob.setAttribute('class', 'hcu-ring__path');
  svg.appendChild(blob);
  /* The frame: a second path in the same coordinate space, centred on the
     ring — the outline of a rounded rectangle sampled at 24 vertices and
     smoothed the way the blob is. A circle is a rounded rect whose corner
     radius is half its size, so it starts as the circle the blob was and
     eases into the photograph's box: one shape, no cross-fade. Once settled
     its vertices breathe on their own noise walks, as demo 4's stuck ring
     does. The svg's overflow is visible, so a box far larger than the
     200-unit viewBox still draws. */
  var frameRect = document.createElementNS(NS, 'path');
  frameRect.setAttribute('class', 'hcu-frame');
  svg.appendChild(frameRect);
  ring.appendChild(svg);

  var arrow = document.createElementNS(NS, 'svg');
  arrow.setAttribute('class', 'hcu-arrow');
  /* The reference arrow is an OUTLINE, not a line with a chevron on it: the
     silhouette of a long shaft and an open triangular head, stroked around its
     own perimeter. That is what gives it the drawn, drafting-table quality —
     a stroked polyline reads as an icon, a stroked outline reads as a mark.
     One closed path, traced shaft -> head -> shaft. */
  arrow.setAttribute('viewBox', '0 0 117.25 86.75');
  var apath = document.createElementNS(NS, 'path');
  apath.setAttribute('class', 'hcu-arrow__path');
  apath.setAttribute('d', 'M111.45,42.5,74.65,5.7l-9.9,9.9,20.6,20.6H6.45v14h78.9L64.75,70.8l9.9,9.9,36.8-36.8A1,1,0,0,0,111.45,42.5Z');
  arrow.appendChild(apath);
  ring.appendChild(arrow);

  /* A big thin cross for photographs that open. Drawn as two hairlines in the
     ring's own coordinate space so it scales and spins with the ring rather
     than sitting on top of it as a fixed glyph. Kept deliberately large and
     light — it reads as "this opens", not as a button. */
  var plus = document.createElementNS(NS, 'svg');
  plus.setAttribute('class', 'hcu-plus');
  plus.setAttribute('viewBox', '0 0 100 100');
  var pv = document.createElementNS(NS, 'path');
  pv.setAttribute('class', 'hcu-plus__path');
  pv.setAttribute('d', 'M50 18V82M18 50H82');
  plus.appendChild(pv);
  ring.appendChild(plus);

  /* ── blob geometry ─────────────────────────────────────────────────────── */

  /* Catmull-Rom through the vertices, converted to cubic beziers. This is the
     job paper.js's polygon.smooth() does every frame in demo 4: fit a curve
     THROUGH the points rather than treating them as bezier handles, so a
     displaced vertex bulges the outline instead of kinking it. */
  function pathFrom(pts) {
    var n = pts.length, d = 'M' + pts[0][0].toFixed(2) + ' ' + pts[0][1].toFixed(2);
    for (var i = 0; i < n; i++) {
      var p0 = pts[(i - 1 + n) % n], p1 = pts[i], p2 = pts[(i + 1) % n], p3 = pts[(i + 2) % n];
      var c1x = p1[0] + (p2[0] - p0[0]) / 6, c1y = p1[1] + (p2[1] - p0[1]) / 6;
      var c2x = p2[0] - (p3[0] - p1[0]) / 6, c2y = p2[1] - (p3[1] - p1[1]) / 6;
      d += 'C' + c1x.toFixed(2) + ' ' + c1y.toFixed(2) +
           ',' + c2x.toFixed(2) + ' ' + c2y.toFixed(2) +
           ',' + p2[0].toFixed(2) + ' ' + p2[1].toFixed(2);
    }
    return d + 'Z';
  }

  /* A point `s` along the perimeter of a w×h rounded rect (corner radius c),
     clockwise from the top-left corner's end of the top edge, with its
     outward normal: [x, y, nx, ny]. */
  function rectPoint(s, w, h, c) {
    var ew = Math.max(0, w - 2 * c), eh = Math.max(0, h - 2 * c), arc = Math.PI * c / 2;
    var hw = w / 2, hh = h / 2, a;
    if (s < ew) return [-hw + c + s, -hh, 0, -1];
    s -= ew;
    if (s < arc) { a = -Math.PI / 2 + s / c; return [hw - c + Math.cos(a) * c, -hh + c + Math.sin(a) * c, Math.cos(a), Math.sin(a)]; }
    s -= arc;
    if (s < eh) return [hw, -hh + c + s, 1, 0];
    s -= eh;
    if (s < arc) { a = s / c; return [hw - c + Math.cos(a) * c, hh - c + Math.sin(a) * c, Math.cos(a), Math.sin(a)]; }
    s -= arc;
    if (s < ew) return [hw - c - s, hh, 0, 1];
    s -= ew;
    if (s < arc) { a = Math.PI / 2 + s / c; return [-hw + c + Math.cos(a) * c, hh - c + Math.sin(a) * c, Math.cos(a), Math.sin(a)]; }
    s -= arc;
    if (s < eh) return [-hw, hh - c - s, -1, 0];
    s -= eh;
    a = Math.PI + s / c;
    return [-hw + c + Math.cos(a) * c, -hh + c + Math.sin(a) * c, Math.cos(a), Math.sin(a)];
  }

  var DIAG = Math.SQRT1_2;
  var CORNER_N = [[-DIAG, -DIAG], [DIAG, -DIAG], [DIAG, DIAG], [-DIAG, DIAG]];   /* TL, TR, BR, BL */

  /* `amt` 0..1 is how far the shape has grown into its box: the irregularity
     and the breathing scale with it, so the circle the frame starts from is
     a circle and the frame letting go becomes one again. */
  function drawFrame(w, h, c, amt) {
    var cc = Math.min(c, w / 2, h / 2);
    var ew = Math.max(0, w - 2 * cc), eh = Math.max(0, h - 2 * cc), arc = Math.PI * cc / 2;
    var per = 2 * ew + 2 * eh + 4 * arc;
    /* Each side runs from one corner's middle to the next: TL, TR, BR, BL. */
    var mids = [per - arc / 2, ew + arc / 2, ew + arc + eh + arc / 2, 2 * ew + 2 * arc + eh + arc / 2];
    var t = performance.now() / FRAME_NOISE_MS;
    var pts = [], j, q, i, s, len, p, nx, ny, off;
    for (j = 0; j < 4; j++) {
      len = ((mids[(j + 1) % 4] - mids[j]) % per + per) % per;
      for (q = 0; q < FRAME_PTS; q++) {
        i = j * FRAME_PTS + q;
        fbias[i] += (fbiasT[i] - fbias[i]) * 0.12;
        fpos[i] += (fposT[i] - fpos[i]) * 0.12;
        s = (mids[j] + len * fpos[i]) % per;
        p = rectPoint(s, w, h, cc);
        nx = q === 0 ? CORNER_N[j][0] : p[2];
        ny = q === 0 ? CORNER_N[j][1] : p[3];
        off = amt * (fbias[i] * FRAME_JITTER + noise(t + fseeds[i]) * FRAME_NOISE);
        pts.push([p[0] + nx * off, p[1] + ny * off]);
      }
    }
    var fd = pathFrom(pts);
    if (fd !== lastFrameD) { frameRect.setAttribute('d', fd); lastFrameD = fd; }
  }

  function drawBlob(r, noisy) {
    var pts = [], i, a, rr;
    for (i = 0; i < SEGMENTS; i++) {
      a = (i / SEGMENTS) * Math.PI * 2;
      rr = r;
      if (noisy) rr += noise(frame / NOISE_SCALE + seeds[i]) * NOISE_RANGE;
      pts.push([Math.cos(a) * rr, Math.sin(a) * rr]);
    }
    var d = pathFrom(pts);
    if (d !== lastBlobD) { blob.setAttribute('d', d); lastBlobD = d; }
  }

  /* ── hit testing ───────────────────────────────────────────────────────── */

  function closest(el, sel) {
    return el && el.closest ? el.closest(sel) : null;
  }

  function setState(next) {
    if (state === next) return;
    state = next;
    root.setAttribute('data-hcu', next);
    wake();                            /* a new state has a new radius to ease to */
  }

  /* Start the loop if it is asleep. Everything that can change what the
     cursor draws calls this: a pointer move, a state change, a scroll or a
     resize (a stuck ring lets go, a frame's box moves). */
  function wake() {
    if (raf) return;
    sleeping = false;
    /* ⚠️ Restart the heartbeat here. The last one was written before the
       loop went to sleep — possibly minutes ago — so a watchdog check landing
       between this wake and the first completed frame read it as a dead loop
       and switched the custom cursor off for good (caught in WebKit on the
       VPS, 2026-10-03; about one wake in sixty at 60 Hz). Measured from the
       wake, a loop that really dies is still caught two seconds later. */
    alive = Date.now();
    raf = requestAnimationFrame(tick);
  }

  /* ── the navigation arrow (demo 2) ─────────────────────────────────────── */

  function spinTo(deg, opacity, scale, instant) {
    if (instant) {
      arrow.style.transition = 'none';
      arrow.style.transform = 'rotate(' + deg + 'deg) scale(' + scale + ')';
      arrow.style.opacity = opacity;
      /* Force the instant value to be committed before the transition is put
         back, or the browser coalesces both writes and tweens from the old
         angle — which is exactly the fly-in demo 2 is avoiding. */
      void arrow.getBoundingClientRect();
      arrow.style.transition = '';
      return;
    }
    arrow.style.transform = 'rotate(' + deg + 'deg) scale(' + scale + ')';
    arrow.style.opacity = opacity;
  }

  function navEnter(region, side) {
    var b = region.getBoundingClientRect();
    /* Demo 2's entry angle: which edge you came through decides where the
       arrow spins in from. */
    var start = py < b.top + b.height / 2 ? -135 : (side === 'next' ? 135 : -315);
    navSide = side;
    spinTo(start, 0, 0.5, true);
    requestAnimationFrame(function () {
      spinTo(navSide === 'next' ? 0 : -180, 1, 1, false);
    });
  }

  function navSwitch(side) {
    if (side === navSide) return;
    navSide = side;
    spinTo(side === 'next' ? 0 : -180, 1, 1, false);
  }

  function navLeave(region) {
    var out = -135;
    if (region) {
      var b = region.getBoundingClientRect();
      out = py < b.top + b.height / 2
        ? (navSide === 'next' ? -135 : -45)
        : (navSide === 'next' ? 135 : -315);
    }
    navSide = null;
    spinTo(out, 0, 0.3, false);
  }

  /* Demo 2 bumps the cursor on slideChange. The equivalent event here is the
     click on a pager button, which is also the moment the photograph swaps. */
  function recoil() {
    if (navSide === null) return;
    arrow.style.transition = 'transform 100ms linear, opacity 300ms linear';
    spinTo(navSide === 'next' ? 0 : -180, 1, 0.85, false);
    setTimeout(function () {
      arrow.style.transition = '';
      spinTo(navSide === 'next' ? 0 : -180, 1, 1, false);
    }, 110);
  }

  /* ── pointer handling ──────────────────────────────────────────────────── */

  var navRegion = null;

  function onMove(e) {
    px = e.clientX;
    py = e.clientY;
    wake();

    if (!seen) {
      seen = true;
      rx = px; ry = py;               /* demo 4's unveilCursor: jump, don't fly in */
    }

    var t = e.target;

    /* Ignored regions hand the real cursor back — text carets in the search
       field, zoom-in and grab in the lightbox. Those cursors mean something. */
    if (closest(t, SEL_IGNORE)) {
      root.classList.add('hcu-native');
      stuck = null;
      frameEl = null;
      if (navRegion) { navLeave(navRegion); navRegion = null; }
      setState('off');
      return;
    }
    root.classList.remove('hcu-native');

    /* The plus marks photographs that actually open. data-media is what the
       preview reads, so a slide without it opens nothing and must not promise
       that it does. Evaluated here — before the nav/link branches — because
       every one of those ends in `return`, and the first version of this sat
       inside the early-return 'off' branch and never ran at all. */
    var slide = closest(t, '.gallery__slide');
    var frame = closest(t, '[data-pe-frame]');
    /* Collection tiles need data-media (that is what the preview pages
       through). Product frames need a real opener button injected over them —
       without it the photograph does not open and the plus would be lying. */
    var opens = (slide && slide.getAttribute('data-media')) ||
                (frame && frame.querySelector('.hi-lb-open'));
    if (opens) root.setAttribute('data-hcu-plus', '1');
    else root.removeAttribute('data-hcu-plus');

    var nav = closest(t, SEL_NAV);
    if (nav) {
      var nb = nav.getBoundingClientRect();
      var side = closest(t, SEL_NEXT) ? 'next'
               : closest(t, SEL_PREV) ? 'prev'
               : (px > nb.left + nb.width / 2 ? 'next' : 'prev');
      if (navRegion !== nav) { navRegion = nav; navEnter(nav, side); }
      else navSwitch(side);
      stuck = null;
      setState('nav');
      return;
    }
    if (navRegion) { navLeave(navRegion); navRegion = null; }

    /* The product frame, before the link branch: a card's link is also a
       link, and the frame is the richer answer for it. */
    var fr = SEL_FRAME ? closest(t, SEL_FRAME) : null;
    if (fr) {
      frameEl = (FRAME_TARGET && fr.querySelector(FRAME_TARGET)) || fr;
      stuck = null;
      setState('frame');
      return;
    }
    frameEl = null;

    var link = closest(t, SEL_LINK);
    if (link) {
      var b = link.getBoundingClientRect();
      /* Stick only to targets small enough that their centre is still where
         the pointer is. Anything bigger just inflates the ring in place. */
      stuck = (b.width <= STICK_MAX && b.height <= STICK_MAX)
        ? { x: Math.round(b.left + b.width / 2), y: Math.round(b.top + b.height / 2) }
        : null;
      setState('link');
      return;
    }

    stuck = null;
    setState('idle');
  }

  function hide() { frameEl = null; setState('off'); wake(); }

  /* ── the loop ──────────────────────────────────────────────────────────── */

  function tick() {
    raf = 0;
    frame++;

    var framing = state === 'frame' && frameEl && frameEl.isConnected;
    var tx, ty, k;
    if (framing) {
      /* Framing: the ring's centre heads for the photograph's centre and the
         rect grows from the circle it was into the box plus its pad. Read
         every frame, so a scroll under the pointer is followed. */
      var fb = frameEl.getBoundingClientRect();
      tx = fb.left + fb.width / 2;
      ty = fb.top + fb.height / 2;
      if (!frameOn) {
        frameOn = true;
        fw = radius * 2; fh = radius * 2; frx = radius;
        root.setAttribute('data-hcu-frame', '1');
      }
      /* A new card, a new hand-drawn shape: the vertices ease toward it. */
      if (frameEl !== shapedFor) { shapedFor = frameEl; reshape(); }
      ftw = fb.width + FRAME_PAD * 2;
      fth = fb.height + FRAME_PAD * 2;
      fw += (ftw - fw) * FRAME_EASE;
      fh += (fth - fh) * FRAME_EASE;
      frx += (FRAME_RADIUS - frx) * FRAME_EASE;
      k = FOLLOW_STUCK;
    } else {
      tx = stuck ? stuck.x : px;
      ty = stuck ? stuck.y : py;
      k = stuck ? FOLLOW_STUCK : FOLLOW;
    }
    rx += (tx - rx) * k;
    ry += (ty - ry) * k;

    var rTarget = state === 'nav' ? R_NAV : state === 'link' ? R_LINK : R_IDLE;
    radius += (rTarget - radius) * RADIUS_EASE;

    /* Converged: land exactly, so the last frame before sleeping draws the
       resting position and not one a hair short of it. */
    var still = Math.abs(tx - rx) < 0.05 && Math.abs(ty - ry) < 0.05 && Math.abs(rTarget - radius) < 0.02;
    if (still) { rx = tx; ry = ty; radius = rTarget; }

    if (frameOn && !framing) {
      /* Letting go: the rect eases back to the circle the blob is drawing at
         the pointer, and once it is one, the blob takes over. */
      fw += (radius * 2 - fw) * FRAME_EASE;
      fh += (radius * 2 - fh) * FRAME_EASE;
      frx += (radius - frx) * FRAME_EASE;
      if (Math.abs(fw - radius * 2) < 0.75 && Math.abs(fh - radius * 2) < 0.75) {
        frameOn = false;
        root.removeAttribute('data-hcu-frame');
      }
    }
    if (frameOn) {
      /* Demo 4 starts the noise only once stuck AND at full size. Here the
         shape and the breathing grow in with the frame (`amt`) and fade out
         as it lets go, so neither the circle nor the hand-over ever wobbles. */
      var gap = (Math.abs(ftw - fw) + Math.abs(fth - fh)) / Math.max(1, 0.25 * (ftw + fth));
      drawFrame(fw, fh, frx, Math.max(0, Math.min(1, 1 - gap)));
    }

    /* Demo 4 only starts the noise once stuck AND at full size. Same gate: a
       ring still growing has no business wobbling. */
    var noisy = NOISE_RANGE > 0 && state === 'link' && radius > R_LINK - 1.5;
    drawBlob(radius, noisy);

    var dotT = 'translate3d(' + px.toFixed(2) + 'px,' + py.toFixed(2) + 'px,0)';
    var ringT = 'translate3d(' + rx.toFixed(2) + 'px,' + ry.toFixed(2) + 'px,0)';
    if (dotT !== lastDotT) { dot.style.transform = dotT; lastDotT = dotT; }
    if (ringT !== lastRingT) { ring.style.transform = ringT; lastRingT = ringT; }

    /* The heartbeat is written only by a frame that ran to the end, so a loop
       that throws mid-frame still goes quiet for the watchdog. */
    alive = Date.now();

    /* Sleep when nothing would change: the ring has caught up, the radius has
       settled, no noise is walking and no frame is drawn (a frame and the
       link state's noise animate by design, so they keep the loop awake). */
    if (still && !noisy && !frameOn && !framing) { sleeping = true; return; }
    raf = requestAnimationFrame(tick);
  }

  /* ── boot ──────────────────────────────────────────────────────────────── */

  try {
    document.body.appendChild(ring);
    document.body.appendChild(dot);
    if (!ring.isConnected || !dot.isConnected) return;

    drawBlob(R_IDLE, false);
    root.setAttribute('data-hcu', 'off');

    document.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerdown', function (e) {
      if (closest(e.target, SEL_NAV)) recoil();
    }, { passive: true });
    /* Leaving the window: mouseleave does not bubble, so it is bound on the
       element the pointer actually exits through as well as on document.
       Coming back needs no listener — the next pointermove restates. */
    document.addEventListener('mouseleave', hide);
    root.addEventListener('mouseleave', hide);
    window.addEventListener('blur', hide);

    /* Drawers, overlays and route changes can move a stuck target out from
       under a captured ring. Cheapest correct answer: drop the capture. */
    window.addEventListener('scroll', function () { stuck = null; wake(); }, { passive: true });   /* a stuck ring lets go; a framed box moves */
    window.addEventListener('resize', function () { stuck = null; wake(); }, { passive: true });

    alive = Date.now();
    wake();

    if (cfg.blend) root.classList.add('hcu-blend');

    /* LAST. Everything above is inert; this is the line that hides the system
       cursor, and it only runs if none of it threw. */
    root.classList.add('hcu-on');

    /* Watchdog. If the loop stops for any reason after the class is on, the
       page would be left with a frozen custom cursor and no real one. Two
       seconds of silence and the native cursor comes back for good. */
    setInterval(function () {
      if (!root.classList.contains('hcu-on')) return;
      /* A loop that put itself to sleep is quiet on purpose; only a loop that
         was meant to be running and has not finished a frame is dead. */
      if (sleeping) return;
      if (Date.now() - alive > 2000 && document.visibilityState === 'visible') {
        root.classList.remove('hcu-on');
        root.setAttribute('data-hcu', 'off');
      }
    }, 1000);
  } catch (err) {
    root.classList.remove('hcu-on');
    if (ring.parentNode) ring.parentNode.removeChild(ring);
    if (dot.parentNode) dot.parentNode.removeChild(dot);
  }
})();
